import { ref, computed } from 'vue';
import familiaService from '@/services/perfil/familia';

export function useFamilia() {
    const familia = ref(null);
    const miembros = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const expandedId = ref(null);
    const currentAbonadoId = ref(null);

    // Fuente de verdad única para paginación — mismo patrón que useAbonos.
    const meta = ref({ current_page: 1, last_page: 1, total: 0, per_page: 5 });

    const page    = computed(() => meta.value.current_page);
    const perPage = computed(() => meta.value.per_page);

    const isEmpty = computed(() => familia.value === null || miembros.value.length === 0);

    const miembroCount = computed(() => meta.value.total ?? miembros.value.length);

    const isTitular = miembroId => {
        return familia.value?.titular?.id === miembroId;
    };

    const fetchFamilia = async abonadoId => {
        loading.value = true;
        error.value = null;
        currentAbonadoId.value = abonadoId;
        try {
            const result = await familiaService.getFamiliaByAbonado(abonadoId);
            familia.value = result;
            // Fetch members with pagination from backend
            await fetchMiembros(abonadoId, 1, meta.value.per_page);
        } catch (err) {
            error.value = err?.message ?? 'Error al cargar el grupo familiar';
            familia.value = null;
            miembros.value = [];
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useFamilia] Error al obtener familia:', err);
            }
        } finally {
            loading.value = false;
        }
    };

    const fetchMiembros = async(abonadoId, newPage = page.value, newPerPage = perPage.value) => {
        loading.value = true;
        error.value = null;
        try {
            const result = await familiaService.getMiembrosFamilia(abonadoId, newPage, newPerPage);
            miembros.value = result.items;
            meta.value = result.meta ?? { current_page: 1, last_page: 1, total: 0, per_page: newPerPage };
        } catch (err) {
            error.value = err?.message ?? 'Error al cargar los miembros';
            miembros.value = [];
            meta.value = { current_page: 1, last_page: 1, total: 0, per_page: newPerPage };
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useFamilia] Error al obtener miembros:', err);
            }
        } finally {
            loading.value = false;
        }
    };

    const validarMiembro = async(numeroAbonado, dni) => {
        try {
            if (!currentAbonadoId.value) throw new Error('No hay abonado activo');
            return await familiaService.validarMiembro(currentAbonadoId.value, numeroAbonado, dni);
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useFamilia] Error al validar miembro:', err);
            }
            throw err;
        }
    };

    const crearFamilia = async(nombre, abonadoId) => {
        const targetId = abonadoId ?? currentAbonadoId.value;
        if (!targetId) throw new Error('No hay abonado activo');
        try {
            const result = await familiaService.crearFamilia(targetId, nombre);
            expandedId.value = null;
            if (currentAbonadoId.value) {
                await fetchFamilia(currentAbonadoId.value);
            }
            return result;
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useFamilia] Error al crear familia:', err);
            }
            throw err;
        }
    };

    const agregarMiembro = async(numeroAbonado, dni) => {
        try {
            if (!currentAbonadoId.value) throw new Error('No hay abonado activo');
            await familiaService.agregarMiembro(currentAbonadoId.value, numeroAbonado, dni);
            expandedId.value = null;
            if (currentAbonadoId.value) {
                await fetchFamilia(currentAbonadoId.value);
            }
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useFamilia] Error al agregar miembro:', err);
            }
            throw err;
        }
    };

    const actualizarNombre = async nombre => {
        try {
            if (!currentAbonadoId.value) throw new Error('No hay abonado activo');
            const result = await familiaService.actualizarNombre(currentAbonadoId.value, nombre);
            if (currentAbonadoId.value) {
                await fetchFamilia(currentAbonadoId.value);
            }
            return result;
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useFamilia] Error al actualizar nombre:', err);
            }
            throw err;
        }
    };

    const removerMiembro = async miembroId => {
        try {
            if (!currentAbonadoId.value) throw new Error('No hay abonado activo');
            const result = await familiaService.removerMiembro(currentAbonadoId.value, miembroId);
            if (result.deleted) {
                // La familia se eliminó
                familia.value = null;
                miembros.value = [];
                return result;
            }
            // La familia sobrevive, refrescar miembros
            if (currentAbonadoId.value) {
                await fetchMiembros(currentAbonadoId.value, page.value, perPage.value);
            }
            return result;
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useFamilia] Error al remover miembro:', err);
            }
            throw err;
        }
    };

    const toggleExpand = miembroId => {
        expandedId.value = expandedId.value === miembroId ? null : miembroId;
    };

    return {
        familia,
        miembros,
        isEmpty,
        miembroCount,
        loading,
        error,
        expandedId,
        meta,
        page,
        perPage,
        isTitular,
        fetchFamilia,
        fetchMiembros,
        validarMiembro,
        crearFamilia,
        agregarMiembro,
        removerMiembro,
        actualizarNombre,
        toggleExpand,
    };
}
